import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { formatCurrency } from '@/lib/format';
import { toApiDate, toStoreTime } from '@/lib/store-time';
import { UserPicker, type PickedUser } from './user-picker';
import { useVoucherAction } from './vouchers.api';
import type { AdminVoucherDetail } from './types';

export type VoucherAction =
  | 'resend'
  | 'email'
  | 'reassign'
  | 'extend'
  | 'void'
  | 'refund';

/** Destructive ones get the destructive button. */
const DESTRUCTIVE: VoucherAction[] = ['void', 'refund'];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
    </div>
  );
}

/** `yyyy-MM-dd` in store time, for a date input. */
function toDateInput(value: string | null | undefined) {
  const date = toStoreTime(value);
  return date ? date.toISOString().slice(0, 10) : '';
}

function threeMonthsFromNow() {
  const date = new Date();
  date.setMonth(date.getMonth() + 3);
  return date.toISOString().slice(0, 10);
}

/**
 * Every per-voucher action, in one dialog shaped by `action`. Kept together
 * because they share the same frame — a sentence saying what will happen, the
 * fields, an optional reason — and the same call pattern against
 * `v2/admin/vouchers/{id}/{action}`.
 *
 * Remounted per action by the caller (it is keyed on the action), so every
 * field starts from the voucher as it is now rather than from last time.
 */
export function VoucherActionDialog({
  voucher,
  action,
  onClose,
}: {
  voucher: AdminVoucherDetail;
  action: VoucherAction;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const mutation = useVoucherAction<Record<string, unknown> | undefined>(
    voucher.id,
    action
  );

  const [reason, setReason] = useState('');
  const [email, setEmail] = useState(voucher.recipientEmail ?? '');
  const [recipientName, setRecipientName] = useState(voucher.recipientName ?? '');
  const [language, setLanguage] = useState(String(voucher.recipientLanguage));
  const [user, setUser] = useState<PickedUser | null>(null);
  const [noExpiry, setNoExpiry] = useState(!voucher.expiresAt);
  const [expiresOn, setExpiresOn] = useState(() => {
    // An expired voucher is being extended: start from three months out, not
    // from a date already in the past.
    const current = toDateInput(voucher.expiresAt);
    return current && !voucher.isExpired ? current : threeMonthsFromNow();
  });
  const refundable = Math.max(0, voucher.amountPaid - voucher.amountRefunded);
  const [amount, setAmount] = useState(voucher.suggestedRefund);

  const needsReason = action === 'void';
  const invalid =
    (action === 'email' && !email.trim()) ||
    (action === 'reassign' && !user) ||
    (action === 'extend' && !noExpiry && !expiresOn) ||
    (action === 'void' && !reason.trim()) ||
    (action === 'refund' && (amount <= 0 || amount > refundable));

  function body(): Record<string, unknown> | undefined {
    const why = reason.trim() || null;
    switch (action) {
      case 'email':
        return {
          email: email.trim(),
          recipientName: recipientName.trim() || null,
          recipientLanguage: Number(language),
          reason: why,
        };
      case 'reassign':
        return { userId: user!.id, reason: why };
      case 'extend':
        return {
          expiresAt: noExpiry ? null : toApiDate(expiresOn, true),
          reason: why,
        };
      case 'void':
        return { reason: why };
      case 'refund':
        return { amount, reason: why };
      default:
        return undefined;
    }
  }

  function submit() {
    mutation.mutate(body(), {
      onSuccess: () => {
        toast.success(t(`vouchers.actionDialog.${action}.done`));
        onClose();
      },
      onError: (error) =>
        toast.error(
          error instanceof Error ? error.message : t('vouchers.actionFailed')
        ),
    });
  }

  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t(`vouchers.actionDialog.${action}.title`)}</AlertDialogTitle>
          <AlertDialogDescription>
            {t(`vouchers.actionDialog.${action}.body`, {
              email: voucher.recipientEmail ?? '',
              holder: voucher.holder?.name ?? '',
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          {action === 'email' && (
            <>
              <Field label={t('vouchers.recipientEmail')}>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t('vouchers.recipientName')}>
                  <Input
                    value={recipientName}
                    maxLength={200}
                    onChange={(e) => setRecipientName(e.target.value)}
                  />
                </Field>
                <Field label={t('vouchers.emailLanguage')}>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">English</SelectItem>
                      <SelectItem value="1">Español</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </>
          )}

          {action === 'reassign' && (
            <Field label={t('vouchers.newHolder')}>
              <UserPicker value={user} onChange={setUser} />
            </Field>
          )}

          {action === 'extend' && (
            <>
              <div className="flex items-center justify-between gap-4">
                <Label className="text-xs font-semibold">
                  {t('vouchers.noExpiry')}
                </Label>
                <Switch checked={noExpiry} onCheckedChange={setNoExpiry} />
              </div>
              {!noExpiry && (
                <Field label={t('vouchers.newExpiry')}>
                  <Input
                    type="date"
                    value={expiresOn}
                    onChange={(e) => setExpiresOn(e.target.value)}
                  />
                </Field>
              )}
            </>
          )}

          {action === 'refund' && (
            <Field label={t('vouchers.refundAmount')}>
              <Input
                type="number"
                min={0}
                max={refundable}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
              />
              <p className="text-muted-foreground text-xs">
                {t('vouchers.refundHint', {
                  suggested: formatCurrency(voucher.suggestedRefund),
                  refundable: formatCurrency(refundable),
                })}
              </p>
            </Field>
          )}

          {action !== 'resend' && (
            <Field
              label={needsReason ? t('vouchers.reasonRequired') : t('vouchers.reason')}
            >
              <Textarea
                rows={2}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <Button
            variant={DESTRUCTIVE.includes(action) ? 'destructive' : 'default'}
            disabled={invalid || mutation.isPending}
            onClick={submit}
          >
            {mutation.isPending && <Loader2 className="animate-spin" />}
            {t(`vouchers.actionDialog.${action}.confirm`)}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
